import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useAuth } from './auth/AuthProvider';
import { LoginPage, SignupPage } from './auth/AuthPages';
import { RequireArea, RequireAuth } from './auth/guards';
import { homePathFor, type Area } from './auth/roles';
import { EmptyState } from './components';
import { AccountPage } from './pages/AccountPage';
import { AppLayout } from './pages/AppLayout';
import { AuditPage } from './pages/AuditPage';
import { ClientDetailPage } from './pages/clients/ClientDetailPage';
import { ClientsPage } from './pages/clients/ClientsPage';
import { DashboardPage } from './pages/DashboardPage';
import { KbDocPage } from './pages/kb/KbDocPage';
import { KbPage } from './pages/kb/KbPage';
import { MeetingDetailPage } from './pages/meetings/MeetingDetailPage';
import { MeetingPrintPage } from './pages/meetings/MeetingPrintPage';
import { MeetingsPage } from './pages/meetings/MeetingsPage';
import { SettingsPage } from './pages/SettingsPage';
import { UsagePage } from './pages/UsagePage';
import { UsersPage } from './pages/UsersPage';

function Home() {
  const { user } = useAuth();
  return <Navigate to={user ? homePathFor(user.role) : '/login'} replace />;
}

const area = (a: Area, el: ReactNode) => <RequireArea area={a}>{el}</RequireArea>;

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route
        path="/meetings/:id/print"
        element={
          <RequireAuth>
            <MeetingPrintPage />
          </RequireAuth>
        }
      />
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Home />} />
        <Route path="dashboard" element={area('dashboard', <DashboardPage />)} />
        <Route path="meetings" element={area('meetings', <MeetingsPage scope="visible" />)} />
        <Route path="my-meetings" element={area('myMeetings', <MeetingsPage scope="mine" />)} />
        {/* Detail is reachable from both lists; the server decides visibility. */}
        <Route path="meetings/:id" element={<MeetingDetailPage />} />
        <Route path="clients" element={area('clients', <ClientsPage />)} />
        <Route path="clients/:id" element={area('clients', <ClientDetailPage />)} />
        <Route path="users" element={area('users', <UsersPage />)} />
        <Route path="usage" element={area('usage', <UsagePage />)} />
        <Route path="kb" element={area('kb', <KbPage />)} />
        <Route path="kb/new" element={area('kb', <KbDocPage />)} />
        <Route path="kb/:id" element={area('kb', <KbDocPage />)} />
        <Route path="settings" element={area('settings', <SettingsPage />)} />
        <Route path="audit" element={area('audit', <AuditPage />)} />
        <Route path="account" element={area('account', <AccountPage />)} />
        <Route path="*" element={<EmptyState title="Page not found">Check the address or use the menu.</EmptyState>} />
      </Route>
    </Routes>
  );
}
